"use client";

/**
 * NotificationPreferencesCard
 *
 * Inspired by the navbar notifications dropdown — each notification type
 * is rendered as a row with a colored icon chip (matching the dropdown's
 * visual language), subject + body, and a Switch on the right.
 *
 * Autosaves on toggle (no Save button). SMS channel removed — we don't
 * actually send SMS notifications.
 */

import { useEffect, useMemo, useRef, useState } from "react";
import { AnimatePresence, motion } from "framer-motion";
import {
  Mail,
  UserPlus,
  CalendarClock,
  BarChart3,
  Bell,
  Loader2,
  CheckCircle2,
  AlertCircle,
  Volume2,
} from "lucide-react";

import { cn } from "@/app/_module/lib/utils";
import { Card, CardContent } from "@/app/_module/components/ui/card";
import { Label } from "@/app/_module/components/ui/label";
import { Switch } from "@/app/_module/components/ui/switch";
import { useToast } from "@/app/_module/hooks/use-toast";
import { useUserContext } from "@/app/_module/store/contexts/userContext";
import { useNotificationSoundPreference } from "@/app/_module/hooks/use-notification-sound";
import { notificationPreferencesService } from "../../services/notificationPreferencesService";
import type {
  NotificationPreferences,
  UpdateNotificationPreferencesRequest,
} from "../../types/NotificationPreferences";

// ────────────────────────────────────────────────────────────
// Config — rows grouped in two sections
// ────────────────────────────────────────────────────────────

type PrefKey = keyof UpdateNotificationPreferencesRequest;

interface Row {
  key: PrefKey;
  label: string;
  description: string;
  Icon: React.ComponentType<{ className?: string; strokeWidth?: number }>;
  /** Tailwind classes for the icon tile when enabled. */
  tone: {
    bg: string;
    bgSoft: string;
    text: string;
    ring: string;
  };
  defaultValue?: boolean;
}

const CHANNEL_ROWS: Row[] = [
  {
    key: "shouldReceiveEmail",
    label: "Email",
    description: "Receive important activity and alerts in your inbox.",
    Icon: Mail,
    tone: {
      bg: "bg-purple-500",
      bgSoft: "bg-purple-50",
      text: "text-purple-600",
      ring: "ring-purple-200",
    },
    defaultValue: true,
  },
];

const EVENT_ROWS: Row[] = [
  {
    key: "shouldReceiveLeadAssignments",
    label: "Lead assignments",
    description: "When a new lead is assigned to you by your agency or a rule.",
    Icon: UserPlus,
    tone: {
      bg: "bg-emerald-500",
      bgSoft: "bg-emerald-50",
      text: "text-emerald-600",
      ring: "ring-emerald-200",
    },
    defaultValue: true,
  },
  {
    key: "shouldReceiveUpcomingAppointments",
    label: "Appointment reminders",
    description: "A nudge before each scheduled appointment so you're never late.",
    Icon: CalendarClock,
    tone: {
      bg: "bg-amber-500",
      bgSoft: "bg-amber-50",
      text: "text-amber-600",
      ring: "ring-amber-200",
    },
    defaultValue: true,
  },
  {
    key: "shouldReceiveWeeklyReports",
    label: "Weekly recap",
    description: "Every Monday — calls, appointments, and conversions last week.",
    Icon: BarChart3,
    tone: {
      bg: "bg-blue-500",
      bgSoft: "bg-blue-50",
      text: "text-blue-600",
      ring: "ring-blue-200",
    },
    defaultValue: true,
  },
];

const SOUND_ROW: Row = {
  key: "shouldPlayNotificationSound",
  label: "Notification sounds",
  description: "Play a subtle chime when new notifications arrive in-app.",
  Icon: Volume2,
  tone: {
    bg: "bg-slate-600",
    bgSoft: "bg-slate-100",
    text: "text-slate-700",
    ring: "ring-slate-200",
  },
  defaultValue: true,
};

// ────────────────────────────────────────────────────────────
// Component
// ────────────────────────────────────────────────────────────

export function NotificationPreferencesCard() {
  const { user, refetchUser } = useUserContext();
  const { toast } = useToast();
  const { setSoundPreference } = useNotificationSoundPreference();

  const readInitial = (): Record<PrefKey, boolean> => {
    const prefs = (user?.notificationPreferences || {}) as Partial<NotificationPreferences>;
    const val = (k: PrefKey, fallback: boolean) =>
      typeof prefs[k] === "boolean" ? (prefs[k] as boolean) : fallback;
    return {
      shouldReceiveEmail: val("shouldReceiveEmail", true),
      shouldReceiveSms: val("shouldReceiveSms", false),
      shouldReceiveLeadAssignments: val("shouldReceiveLeadAssignments", true),
      shouldReceiveUpcomingAppointments: val("shouldReceiveUpcomingAppointments", true),
      shouldReceiveWeeklyReports: val("shouldReceiveWeeklyReports", true),
      // Mobile push — TRUE by default, matching the backend column defaults, so
      // existing users aren't silently muted when the app ships.
      shouldReceivePushNotifications: val("shouldReceivePushNotifications", true),
      shouldReceiveBroadcasts: val("shouldReceiveBroadcasts", true),
      shouldPlayNotificationSound: val("shouldPlayNotificationSound", true),
    };
  };

  const [state, setState] = useState<Record<PrefKey, boolean>>(readInitial);
  const [saveStatus, setSaveStatus] = useState<"idle" | "saving" | "saved" | "error">("idle");
  const mountedRef = useRef(false);
  const pendingRef = useRef<UpdateNotificationPreferencesRequest>({});
  const timerRef = useRef<NodeJS.Timeout | null>(null);

  // One-shot refresh from server on mount (prefs may have changed elsewhere)
  useEffect(() => {
    let cancelled = false;
    notificationPreferencesService
      .getNotificationPreferences()
      .then((prefs) => {
        if (cancelled) return;
        setState((prev) => ({
          ...prev,
          shouldReceiveEmail: prefs.shouldReceiveEmail ?? prev.shouldReceiveEmail,
          shouldReceiveSms: prefs.shouldReceiveSms ?? prev.shouldReceiveSms,
          shouldReceiveLeadAssignments:
            prefs.shouldReceiveLeadAssignments ?? prev.shouldReceiveLeadAssignments,
          shouldReceiveUpcomingAppointments:
            prefs.shouldReceiveUpcomingAppointments ?? prev.shouldReceiveUpcomingAppointments,
          shouldReceiveWeeklyReports:
            prefs.shouldReceiveWeeklyReports ?? prev.shouldReceiveWeeklyReports,
          shouldPlayNotificationSound:
            prefs.shouldPlayNotificationSound ?? prev.shouldPlayNotificationSound,
        }));
        refetchUser((prevUser) => ({ ...prevUser, notificationPreferences: prefs }));
      })
      .catch(() => {});
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    mountedRef.current = true;
    return () => {
      mountedRef.current = false;
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleToggle = (key: PrefKey, next: boolean) => {
    setState((s) => ({ ...s, [key]: next }));
    pendingRef.current[key] = next;

    if (key === "shouldPlayNotificationSound") setSoundPreference(next);

    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(async () => {
      const payload = pendingRef.current;
      pendingRef.current = {};
      setSaveStatus("saving");
      try {
        await notificationPreferencesService.updateNotificationPreferences(payload);
        refetchUser((prev) => ({
          ...prev,
          notificationPreferences: prev.notificationPreferences
            ? ({ ...prev.notificationPreferences, ...payload } as NotificationPreferences)
            : undefined,
        }));
        setSaveStatus("saved");
        setTimeout(() => mountedRef.current && setSaveStatus("idle"), 1800);
      } catch (e) {
        setSaveStatus("error");
        toast({
          title: "Couldn't save",
          description: e instanceof Error ? e.message : "Please try again.",
          variant: "destructive",
        });
        setTimeout(() => mountedRef.current && setSaveStatus("idle"), 2400);
      }
    }, 500);
  };

  const onCount = useMemo(() => {
    const keys: PrefKey[] = [
      "shouldReceiveEmail",
      "shouldReceiveLeadAssignments",
      "shouldReceiveUpcomingAppointments",
      "shouldReceiveWeeklyReports",
      "shouldPlayNotificationSound",
    ];
    return keys.filter((k) => state[k]).length;
  }, [state]);
  const totalCount = 5;

  const renderRow = (row: Row) => {
    const checked = !!state[row.key];
    const { Icon, tone } = row;
    return (
      <div
        key={row.key}
        className={cn(
          "relative rounded-xl border transition-all duration-300",
          "hover:shadow-md hover:shadow-black/[0.03]",
          checked ? "border-primary/20 bg-primary/[0.015]" : "border-border",
        )}
      >
        {/* Left accent bar — matches DialIQ / other preference cards */}
        <div
          className={cn(
            "absolute left-0 top-3 bottom-3 w-[3px] rounded-full transition-all duration-500",
            checked ? "bg-primary" : "bg-transparent",
          )}
        />

        <div className="flex items-center justify-between gap-4 p-4 pl-5">
          <div className="flex items-center gap-4 min-w-0">
            {/* Icon tile — navbar-dropdown style: colored chip when on, muted when off */}
            <div
              className={cn(
                "flex items-center justify-center h-10 w-10 rounded-lg ring-1 ring-inset transition-all duration-300",
                checked ? cn(tone.bgSoft, tone.text, tone.ring) : "bg-muted text-muted-foreground ring-transparent",
              )}
            >
              <Icon className="h-[18px] w-[18px]" strokeWidth={2.25} />
            </div>

            <div className="min-w-0">
              <Label
                htmlFor={`pref-${row.key}`}
                className="text-[15px] font-semibold leading-none cursor-pointer"
              >
                {row.label}
              </Label>
              <p className="mt-1.5 max-w-md text-sm text-muted-foreground">
                {row.description}
              </p>
            </div>
          </div>

          <Switch
            id={`pref-${row.key}`}
            checked={checked}
            onCheckedChange={(v) => handleToggle(row.key, !!v)}
          />
        </div>
      </div>
    );
  };

  return (
    <Card className="overflow-hidden">
      {/* Header — mirrors navbar dropdown's header bar */}
      <div className="px-6 pt-6 pb-4">
        <div className="flex items-center justify-between gap-3">
          <div className="flex items-center gap-4">
            <div className="relative flex items-center justify-center h-11 w-11 rounded-xl bg-primary text-primary-foreground shadow-sm">
              <Bell className="h-5 w-5" strokeWidth={2.25} />
              {onCount > 0 && (
                <span className="absolute -top-1 -right-1 h-5 min-w-5 px-1 flex items-center justify-center rounded-full bg-emerald-500 text-[10px] font-bold text-white ring-2 ring-background tabular-nums">
                  {onCount}
                </span>
              )}
            </div>
            <div>
              <h2 className="text-xl font-semibold tracking-tight">Notifications</h2>
              <p className="text-sm text-muted-foreground mt-0.5">
                Choose what pings you and how it reaches you.
              </p>
            </div>
          </div>

          <div className="flex items-center gap-3">
            <span className="hidden sm:inline-flex items-center gap-1.5 rounded-full bg-muted px-2.5 py-1 text-[11px] font-semibold text-muted-foreground tabular-nums">
              <span className="h-1.5 w-1.5 rounded-full bg-emerald-500" />
              {onCount} of {totalCount} on
            </span>
            <AnimatePresence mode="wait">
              {saveStatus === "saving" && (
                <motion.span
                  key="saving"
                  initial={{ opacity: 0, x: 4 }}
                  animate={{ opacity: 1, x: 0 }}
                  exit={{ opacity: 0, x: -4 }}
                  className="flex items-center gap-1 text-[11px] font-medium text-muted-foreground"
                >
                  <Loader2 className="h-3 w-3 animate-spin" />
                  Saving
                </motion.span>
              )}
              {saveStatus === "saved" && (
                <motion.span
                  key="saved"
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  className="flex items-center gap-1 text-[11px] font-medium text-emerald-600"
                >
                  <CheckCircle2 className="h-3 w-3" />
                  Saved
                </motion.span>
              )}
              {saveStatus === "error" && (
                <motion.span
                  key="error"
                  initial={{ opacity: 0, scale: 0.92 }}
                  animate={{ opacity: 1, scale: 1 }}
                  exit={{ opacity: 0, scale: 0.92 }}
                  className="flex items-center gap-1 text-[11px] font-medium text-rose-600"
                >
                  <AlertCircle className="h-3 w-3" />
                  Couldn't save
                </motion.span>
              )}
            </AnimatePresence>
          </div>
        </div>

        <div className="mt-5 h-px bg-gradient-to-r from-primary/25 via-primary/10 to-transparent" />
      </div>

      <CardContent className="pt-0 pb-6 space-y-6">
        {/* Section: Delivery */}
        <section>
          <div className="mb-2.5 flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Delivery channel
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-3">{CHANNEL_ROWS.map(renderRow)}</div>
          <p className="mt-2.5 pl-1 text-[11px] text-muted-foreground">
            In-app notifications are always on — you'll see them in the{" "}
            <Bell className="inline h-3 w-3 -mt-0.5" /> bell in the top bar.
          </p>
        </section>

        {/* Section: Event types */}
        <section>
          <div className="mb-2.5 flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Notify me about
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-3">{EVENT_ROWS.map(renderRow)}</div>
        </section>

        {/* Section: Sound */}
        <section>
          <div className="mb-2.5 flex items-center gap-2">
            <span className="text-[11px] font-bold uppercase tracking-wider text-muted-foreground">
              Sound
            </span>
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="space-y-3">{renderRow(SOUND_ROW)}</div>
        </section>
      </CardContent>
    </Card>
  );
}

export default NotificationPreferencesCard;
